import React from 'react';
import { TrendingDown, AlertCircle, CheckCircle2, ArrowRight, DollarSign, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
export interface RefinanceOpportunity {
  loanId: string;
  isEligible: boolean;
  currentRate?: number;
  estimatedNewRate?: number;
  monthlySavings?: number;
  cashOutAvailable?: number;
  breakEvenMonths?: number;
  ineligibleReason?: string;
  estimatedTimeline?: string;
  note?: string;
}
interface RefinanceCardProps {
  opportunity: RefinanceOpportunity;
  propertyAddress: string;
}
export function RefinanceCard({
  opportunity,
  propertyAddress
}: RefinanceCardProps) {
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(val);
  if (opportunity.isEligible) {
    return <motion.div variants={{
      hidden: {
        opacity: 0,
        y: 20
      },
      visible: {
        opacity: 1,
        y: 0
      }
    }} className="bg-white rounded-xl shadow-sm border border-green-200 overflow-hidden relative" data-id="element-125">
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-green-500" data-id="element-126"></div>
        <div className="p-5 pl-6" data-id="element-127">
          <div className="flex justify-between items-start mb-4" data-id="element-128">
            <div data-id="element-129">
              <div className="flex items-center gap-2 mb-1" data-id="element-130">
                <CheckCircle2 className="w-5 h-5 text-green-600" data-id="element-131" />
                <h3 className="text-lg font-bold text-gray-900" data-id="element-132">
                  Refinance Eligible
                </h3>
              </div>
              <p className="text-sm text-gray-500" data-id="element-133">For {propertyAddress}</p>
            </div>
            <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide" data-id="element-134">
              Strong Candidate
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-135">
            <div className="bg-green-50/50 rounded-lg p-4 border border-green-100" data-id="element-136">
              <div className="flex items-center gap-2 text-green-800 mb-2" data-id="element-137">
                <TrendingDown className="w-4 h-4" data-id="element-138" />
                <span className="font-semibold text-sm" data-id="element-139">Rate Reduction</span>
              </div>
              <div className="flex items-baseline gap-2" data-id="element-140">
                <span className="text-gray-500 line-through text-sm" data-id="element-141">
                  {opportunity.currentRate}%
                </span>
                <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-142" />
                <span className="text-2xl font-bold text-green-700" data-id="element-143">
                  {opportunity.estimatedNewRate}%
                </span>
              </div>
              <p className="text-sm text-green-700 mt-1 font-medium" data-id="element-144">
                Est. {formatCurrency(opportunity.monthlySavings || 0)}/mo
                savings
              </p>
            </div>

            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-100" data-id="element-145">
              <div className="flex items-center gap-2 text-brand mb-2" data-id="element-146">
                <DollarSign className="w-4 h-4" data-id="element-147" />
                <span className="font-semibold text-sm" data-id="element-148">
                  Cash-Out Potential
                </span>
              </div>
              <div className="text-2xl font-bold text-brand" data-id="element-149">
                Up to {formatCurrency(opportunity.cashOutAvailable || 0)}
              </div>
              <p className="text-sm text-brand-muted mt-1 font-medium" data-id="element-150">
                Based on 80% LTV
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-151">
            <div className="flex items-center gap-1.5 text-sm text-gray-500" data-id="element-152">
              <Clock className="w-4 h-4" data-id="element-153" />
              <span data-id="element-154">Est. break-even: {opportunity.breakEvenMonths} months</span>
            </div>
            <a href="tel:+12485379200" className="w-full sm:w-auto text-center bg-brand hover:bg-brand-dark text-white px-5 py-2.5 rounded-lg font-semibold transition-colors shadow-sm" data-id="element-155">
              Talk to Sarah About Refinancing
            </a>
          </div>
        </div>
      </motion.div>;
  }
  return <motion.div variants={{
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  }} className="bg-gray-50 rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-156">
      <div className="p-5" data-id="element-157">
        <div className="flex justify-between items-start mb-4" data-id="element-158">
          <div data-id="element-159">
            <div className="flex items-center gap-2 mb-1" data-id="element-160">
              <AlertCircle className="w-5 h-5 text-gray-400" data-id="element-161" />
              <h3 className="text-lg font-bold text-gray-700" data-id="element-162">
                Not Yet Eligible
              </h3>
            </div>
            <p className="text-sm text-gray-500" data-id="element-163">For {propertyAddress}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-100 mb-5" data-id="element-164">
          <p className="text-gray-700 font-medium mb-1" data-id="element-165">
            Reason: {opportunity.ineligibleReason}
          </p>
          <p className="text-sm text-gray-500 mb-3" data-id="element-166">
            Estimated timeline: {opportunity.estimatedTimeline}
          </p>
          <div className="bg-amber-50 text-amber-800 text-sm p-3 rounded-md border border-amber-100" data-id="element-167">
            {opportunity.note}
          </div>
        </div>

        <div className="flex justify-end" data-id="element-168">
          <a href="mailto:sarah@rapidhomelending.com?subject=Question about my loan options" className="w-full sm:w-auto text-center bg-brand hover:bg-brand-dark text-white px-5 py-2.5 rounded-lg font-semibold transition-colors shadow-sm" data-id="element-169">
            Ask Sarah for Options
          </a>
        </div>
      </div>
    </motion.div>;
}